/**
 * Intercepteur Shopify App Proxy pour Développement Local
 * 
 * Intercepte les appels fetch() et XMLHttpRequest vers /apps/api/* 
 * et les redirige vers localhost:8000/api/* avec les paramètres Shopify fake
 * 
 * Actif uniquement si window.SHOPIFY_LOCAL_DEV = true
 * 
 * @version 1.0.0
 */

(function() {
  'use strict';

  // Vérification de l'activation
  if (!window.SHOPIFY_LOCAL_DEV) {
    return;
  }

  // Configuration et constantes
  const constants = {
    URL_PATTERN: /^\/apps\/api(?:\/|$)/,
    DEFAULT_LOCALHOST_PORT: '80',
    DEFAULT_API_PREFIX: '',
    SHOPIFY_PATH_PREFIX: '/apps/api',
    DEFAULT_TIMEOUT: 30000,
    MAX_RETRIES: 3,
    BACKOFF_BASE_MS: 100
  };

  // Fonctions utilitaires
  const utils = {
    // Fonction de détection d'URLs à intercepter
    shouldInterceptUrl: function(url) {
      if (typeof url !== 'string' || !url) {
        return false;
      }
      return constants.URL_PATTERN.test(url);
    },

    // Génération des paramètres Shopify fake
    generateShopifyParams: function() {
      const shopName = window.Shopify?.shop || 'test-store.myshopify.com';
      const customerId = '555';  // Utiliser 555 en dur pour le développement local
      
      return {
        shop: shopName,
        customer_id: customerId,
        logged_in_customer_id: customerId,
        path_prefix: constants.SHOPIFY_PATH_PREFIX,
        timestamp: Date.now().toString()
      };
    },

    // Construction de l'URL de redirection
    buildRedirectUrl: function(originalUrl) {
      // Parser l'URL originale
      let url;
      try {
        // Si l'URL est relative, la rendre absolue pour le parsing
        const baseUrl = window.location.origin;
        url = new URL(originalUrl, baseUrl);
      } catch (e) {
        // Fallback pour URLs malformées
        const parts = originalUrl.split('?');
        const pathname = parts[0];
        const search = parts[1] || '';
        url = {
          pathname: pathname,
          search: search ? '?' + search : '',
          hash: '',
          searchParams: new URLSearchParams(search)
        };
      }

      // Transformer le chemin /apps/api/xxx -> /xxx (sans préfixe)
      let newPathname = url.pathname.replace(constants.SHOPIFY_PATH_PREFIX, constants.DEFAULT_API_PREFIX);
      
      // Construire la nouvelle URL
      const localhostUrl = `http://localhost:${constants.DEFAULT_LOCALHOST_PORT}${newPathname}`;
      const finalUrl = new URL(localhostUrl);
      
      // Préserver les paramètres existants
      if (url.searchParams) {
        url.searchParams.forEach((value, key) => {
          // Préserver l'encodage original pour les caractères spéciaux comme +
          const originalValue = url.search && url.search.includes(`${key}=${encodeURIComponent(value).replace(/%20/g, '+')}`) 
            ? value.replace(/ /g, '+') 
            : value;
          finalUrl.searchParams.set(key, originalValue);
        });
      } else if (url.search) {
        // Fallback pour le parsing manuel
        const searchParams = new URLSearchParams(url.search);
        searchParams.forEach((value, key) => {
          finalUrl.searchParams.set(key, value);
        });
      }
      
      // Ajouter les paramètres Shopify
      const shopifyParams = utils.generateShopifyParams();
      Object.entries(shopifyParams).forEach(([key, value]) => {
        finalUrl.searchParams.set(key, value);
      });
      
      // Ajouter le hash si présent
      if (url.hash) {
        finalUrl.hash = url.hash;
      }
      
      return finalUrl.toString();
    },

    // Fonction de nettoyage d'URL
    cleanUrl: function(url) {
      if (!url || typeof url !== 'string') {
        return url || '';
      }
      
      // Normaliser les chemins
      return url
        .replace(/\/+/g, '/') // Multiples slashes -> un seul
        .replace(/\/\.\//g, '/') // Enlever ./
        .replace(/\/[^\/]+\/\.\.\//g, '/') // Enlever ../
        .replace(/\/$/, '') // Enlever trailing slash sauf pour racine
        .replace(/^$/, '/') // Remplacer string vide par /
        .replace(/^\/\.$/, '/') // Remplacer /. par /
        .replace(/^\/\.\.$/, '/'); // Remplacer /.. par /
    },

    // Validation de configuration
    validateConfig: function() {
      if (!window.PROJECTS_API_CONFIG) {
        return false;
      }
      
      const config = window.PROJECTS_API_CONFIG;
      if (!config.apiUrl || typeof config.apiUrl !== 'string') {
        return false;
      }
      
      try {
        new URL(config.apiUrl);
        return true;
      } catch (e) {
        return false;
      }
    },

    // Parsing d'URL localhost
    parseLocalhostUrl: function(url) {
      try {
        const parsed = new URL(url);
        return {
          host: parsed.hostname,
          port: parsed.port || (parsed.protocol === 'https:' ? '443' : '80'),
          protocol: parsed.protocol,
          pathname: parsed.pathname
        };
      } catch (e) {
        return null;
      }
    },

    // Fusion des headers
    mergeHeaders: function(baseHeaders, additionalHeaders) {
      return Object.assign({}, baseHeaders, additionalHeaders);
    },

    // Logger conditionnel
    logger: {
      log: function(level, message, ...args) {
      },
      
      warn: function(message, ...args) {
        if (window.PROJECTS_API_CONFIG && window.PROJECTS_API_CONFIG.debug) {
          console.warn('[AppProxyInterceptor]', message, ...args);
        }
      },
      
      error: function(message, ...args) {
        if (window.PROJECTS_API_CONFIG && window.PROJECTS_API_CONFIG.debug) {
          console.error('[AppProxyInterceptor]', message, ...args);
        }
      }
    },

    // Gestion d'erreurs
    handleError: function(error) {
      try {
        utils.logger.error('Error handled:', error);
      } catch (e) {
        // Éviter les erreurs en cascade
      }
    },

    // Retry avec backoff exponentiel
    retryWithBackoff: async function(fn, maxRetries = 3, baseDelay = 10) {
      let lastError;
      
      for (let i = 0; i < maxRetries; i++) {
        try {
          return await fn();
        } catch (error) {
          lastError = error;
          if (i < maxRetries - 1) {
            const delay = baseDelay * Math.pow(2, i);
            // S'assurer que le délai est effectivement appliqué
            await new Promise(resolve => {
              setTimeout(() => {
                resolve();
              }, delay);
            });
          }
        }
      }
      
      throw lastError;
    },

    // Créateur d'URL builder (pour éviter les fuites mémoire)
    createUrlBuilder: function() {
      return function(url) {
        return utils.buildRedirectUrl(url);
      };
    }
  };

  // Fonction de configuration par défaut
  function getDefaultConfig() {
    return {
      apiUrl: `http://localhost:${constants.DEFAULT_LOCALHOST_PORT}${constants.DEFAULT_API_PREFIX}`,
      debug: false,
      timeout: constants.DEFAULT_TIMEOUT,
      retries: constants.MAX_RETRIES
    };
  }

  // Sauvegarde des fonctions originales
  const originalFetch = window.fetch;
  const originalXMLHttpRequest = window.XMLHttpRequest;

  // Fonction d'interception de fetch
  function interceptFetch() {
    window.originalFetch = originalFetch;
    
    window.fetch = function(resource, options = {}) {
      const url = typeof resource === 'string' ? resource : resource.url;
      
      if (utils.shouldInterceptUrl(url)) {
        const redirectUrl = utils.buildRedirectUrl(url);
        
        // Créer une nouvelle Request si nécessaire
        if (typeof resource === 'string') {
          return originalFetch(redirectUrl, options);
        } else {
          // Cloner la request avec la nouvelle URL
          const newRequest = new Request(redirectUrl, {
            method: resource.method,
            headers: resource.headers,
            body: resource.body,
            mode: 'cors',
            credentials: resource.credentials,
            cache: resource.cache,
            redirect: resource.redirect,
            referrer: resource.referrer,
            integrity: resource.integrity
          });
          return originalFetch(newRequest);
        }
      }
      
      return originalFetch(resource, options);
    };
  }

  // Fonction d'interception de XMLHttpRequest
  function interceptXMLHttpRequest() {
    window.originalXMLHttpRequest = originalXMLHttpRequest;
    
    window.XMLHttpRequest = function() {
      const xhr = new originalXMLHttpRequest();
      const originalOpen = xhr.open;
      
      xhr.open = function(method, url, async, user, password) {
        if (utils.shouldInterceptUrl(url)) {
          const redirectUrl = utils.buildRedirectUrl(url);
          utils.logger.log('info', 'Intercepting XHR:', url, '->', redirectUrl);
          return originalOpen.call(this, method, redirectUrl, async, user, password);
        }
        
        return originalOpen.call(this, method, url, async, user, password);
      };
      
      return xhr;
    };
  }

  // Classe principale AppProxyInterceptor
  function AppProxyInterceptor() {
    // Fonctions d'instance
    this.shouldInterceptUrl = utils.shouldInterceptUrl;
    this.buildRedirectUrl = utils.buildRedirectUrl;
    this.interceptFetch = interceptFetch;
    this.interceptXMLHttpRequest = interceptXMLHttpRequest;
    this.generateShopifyParams = utils.generateShopifyParams;
    this.cleanUrl = utils.cleanUrl;
    this.validateConfig = utils.validateConfig;
    this.logger = utils.logger;
    this.handleError = utils.handleError;
    this.retryWithBackoff = utils.retryWithBackoff;
    this.restoreOriginals = this.restoreOriginals.bind(this);
    
    // Auto-initialisation
    this.init();
  }

  AppProxyInterceptor.prototype.init = function() {
    interceptFetch();
    interceptXMLHttpRequest();
  };

  AppProxyInterceptor.prototype.restoreOriginals = function() {
    if (window.originalFetch) {
      window.fetch = window.originalFetch;
      delete window.originalFetch;
    }
    if (window.originalXMLHttpRequest) {
      window.XMLHttpRequest = window.originalXMLHttpRequest;
      delete window.originalXMLHttpRequest;
    }
  };

  // Exposition des fonctions utilitaires et constantes
  AppProxyInterceptor.utils = utils;
  AppProxyInterceptor.constants = constants;
  AppProxyInterceptor.getDefaultConfig = getDefaultConfig;

  // Exposition globale
  window.AppProxyInterceptor = AppProxyInterceptor;

  // Auto-instantiation si en mode développement
  if (window.SHOPIFY_LOCAL_DEV) {
    window.proxyInterceptor = new AppProxyInterceptor();
  }

})();